import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";

import Seleccionarpressupost from "./Seleccionarpressupost";
import Detallspressupost from "./Detallspressupost";
import Ofertes from "./Ofertes";
import Versions from "./Versions";
import AjudaPressupost from "./AjudaPressupost";

import "../dashboard_v2.css";

function idPressupost(p) {
  return p ? Number(p.idPressupostos) : null;
}

function PestanyesPressupost({ active, onChange, pucModificar, pucVersions }) {
  const boto = (id, label) => (
    <button
      type="button"
      className={`dv2-subnav-btn ${active === id ? "is-active" : ""}`}
      onClick={() => onChange(id)}
    >
      {label}
    </button>
  );

  return (
    <nav className="dv2-subnav" aria-label="Seccions del pressupost">
      {boto("dades", "Dades")}
      {pucModificar && boto("ofertes", "Ofertes")}
      {pucVersions && boto("versions", "Versions")}
    </nav>
  );
}

function Pressupostos() {
  const location = useLocation();
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucVersions = tincPermis(rolUsuari, "ACCES");

  const [activeTab, setActiveTab] = useState("seleccionar");
  const [selectedPressupost, setSelectedPressupost] = useState(null);
  const [pressupostos, setPressupostos] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const client = location.state?.client || null;
  const clientId = client?.idClients || (typeof client === "number" ? client : null);

  const [filterText, setFilterText] = useState("");
  const [estatFilters, setEstatFilters] = useState(clientId ? "" : "Esborrany");
  const [clientFilter, setClientFilter] = useState(clientId);

  const aplicarPressupostActualitzat = useCallback((actualitzat) => {
    if (!actualitzat) return;
    setSelectedPressupost(actualitzat);
    setPressupostos((prev) =>
      prev.map((p) =>
        idPressupost(p) === idPressupost(actualitzat) ? { ...p, ...actualitzat } : p
      )
    );
  }, []);

  const carregarPressupost = useCallback(async (idPressupostos) => {
    if (!idPressupostos || !empresaSeleccionada?.IdEmpresa) return null;
    try {
      const response = await fetch(`${API_URL}/Pressupostos.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: empresaSeleccionada.IdEmpresa,
          idPressupostos,
        }),
      });
      const result = await response.json();
      if (result.success && result.pressupostos?.length) {
        return result.pressupostos[0];
      }
    } catch (err) {
      console.error("Error recarregant el pressupost:", err);
    }
    return null;
  }, [empresaSeleccionada]);

  const tornarLlista = (refresca = false) => {
    setActiveTab("seleccionar");
    if (refresca) setReloadToken((n) => n + 1);
  };

  const handlePressupostSelect = (pressupost) => {
    if (!pressupost) {
      setSelectedPressupost(null);
      return;
    }
    setSelectedPressupost((prev) =>
      idPressupost(prev) === idPressupost(pressupost) ? null : pressupost
    );
  };

  const obrirFitxa = (pressupost, mode) => {
    setSelectedPressupost(pressupost);
    setActiveTab(mode);
  };

  const canviarSeccio = async (seccio) => {
    if (seccio === "dades") {
      setActiveTab("consultar");
      const id = selectedPressupost?.idPressupostos;
      if (!id) return;
      const actualitzat = await carregarPressupost(id);
      if (actualitzat) aplicarPressupostActualitzat(actualitzat);
      return;
    }
    setActiveTab(seccio);
  };

  const navegarPressupost = useCallback(async (pressupost) => {
    if (!pressupost) return;
    const actualitzat = await carregarPressupost(pressupost.idPressupostos);
    aplicarPressupostActualitzat(actualitzat || pressupost);
  }, [carregarPressupost, aplicarPressupostActualitzat]);

  const esLlista = activeTab === "seleccionar";
  const tabEfectiu = !pucVersions && activeTab === "versions" ? "consultar" : activeTab;
  const mostraPestanyes = selectedPressupost && !["nou", "esborrar"].includes(tabEfectiu);
  const seccioActiva =
    tabEfectiu === "ofertes" || tabEfectiu === "versions" ? tabEfectiu : "dades";
  const subnav = mostraPestanyes ? (
    <PestanyesPressupost
      active={seccioActiva}
      onChange={canviarSeccio}
      pucModificar={pucModificar}
      pucVersions={pucVersions}
    />
  ) : null;

  const subtitolLlista = clientId
    ? "Pressupostos del client seleccionat."
    : "Document comercial d'una proposta. Les versions i les partides es gestionen a dins.";

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaPressupost pestanya="llista" onTancar={() => setMostraAjuda(false)} />}
      {esLlista ? (
        <>
          <div className="dv2-pageHeader">
            <div className="dv2-pageHeader-main">
              <div className="dv2-pageHeader-icon" aria-hidden="true">📄</div>
              <div>
                <h1 className="dv2-pageHeader-title">Pressupostos</h1>
                <p className="dv2-pageHeader-subtitle">{subtitolLlista}</p>
              </div>
            </div>
            <div className="dv2-pageHeader-actions">
              <button
                type="button"
                className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                title="Com funcionen els Pressupostos"
                aria-label="Ajuda"
                onClick={() => setMostraAjuda(true)}
              >
                ?
              </button>
              {pucCrear && (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--primary"
                  onClick={() => {
                    setSelectedPressupost(null);
                    setActiveTab("nou");
                  }}
                >
                  Nou pressupost
                </button>
              )}
            </div>
          </div>

          <Seleccionarpressupost
            onPressupostSelect={handlePressupostSelect}
            onConsultar={(p) => obrirFitxa(p, "consultar")}
            onModificar={(p) => obrirFitxa(p, "modificar")}
            onEsborrar={(p) => obrirFitxa(p, "esborrar")}
            onOfertes={(p) => obrirFitxa(p, "ofertes")}
            onVersions={(p) => obrirFitxa(p, "versions")}
            selectedPressupost={selectedPressupost}
            idClient={client}
            setPressupostos={setPressupostos}
            reloadToken={reloadToken}
            filters={{
              filterText,
              setFilterText,
              estatFilters,
              setEstatFilters,
              clientFilter,
              setClientFilter,
            }}
          />
        </>
      ) : tabEfectiu === "ofertes" ? (
        <Ofertes
          tipus="pressupost"
          entitat={selectedPressupost}
          subnav={subnav}
          onCancel={() => tornarLlista(false)}
        />
      ) : tabEfectiu === "versions" ? (
        <Versions
          pressupost={selectedPressupost}
          subnav={subnav}
          onCancel={() => tornarLlista(false)}
        />
      ) : (
        <Detallspressupost
          mode={tabEfectiu}
          pressupost={tabEfectiu === "nou" ? null : selectedPressupost}
          pressupostos={pressupostos}
          subnav={subnav}
          onNavigate={navegarPressupost}
          onCanviarMode={(modeNou) => setActiveTab(modeNou)}
          onCancel={() => tornarLlista(tabEfectiu === "nou" || tabEfectiu === "esborrar")}
          onSave={async (updated) => {
            if (activeTab === "nou") {
              tornarLlista(true);
              return;
            }
            const idP = updated?.idPressupostos || selectedPressupost?.idPressupostos;
            const actualitzat = await carregarPressupost(idP);
            aplicarPressupostActualitzat(actualitzat || updated);
            setActiveTab("consultar");
          }}
        />
      )}
    </div>
  );
}

export default Pressupostos;
